import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  BarChart3, TrendingUp, Users, Eye, CreditCard, 
  ArrowUpRight, ArrowDownRight, RefreshCw, Calendar,
  Globe, FileText, MousePointer, Target, ArrowRight,
  Clock, Activity, Zap, UserPlus, LogIn, ShoppingCart
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAnalyticsPage = () => {
  const [stats, setStats] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [blogStats, setBlogStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, [period]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, funnelRes, blogRes] = await Promise.all([
        fetch(`${API_URL}/api/analytics/admin/stats?days=${period}`),
        fetch(`${API_URL}/api/analytics/admin/funnel?days=${period}`),
        fetch(`${API_URL}/api/blog/articles?limit=10`)
      ]);
      
      const statsData = await statsRes.json();
      const funnelData = await funnelRes.json();
      const blogData = await blogRes.json();
      
      setStats(statsData);
      setFunnel(funnelData);
      setBlogStats(blogData);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Erreur chargement analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-10 w-10 animate-spin text-blue-500 mx-auto" />
          <p className="mt-4 text-slate-400">Chargement des analytics...</p>
        </div>
      </div>
    );
  }

  const kpis = [
    {
      title: "Visites",
      value: stats?.summary?.page_views || 0,
      icon: Eye,
      bgColor: "bg-blue-500/20",
      iconColor: "text-blue-400",
      description: "Pages vues"
    },
    {
      title: "Inscriptions",
      value: stats?.summary?.signups || 0,
      icon: UserPlus,
      bgColor: "bg-emerald-500/20",
      iconColor: "text-emerald-400",
      description: "Nouveaux comptes"
    },
    {
      title: "Abonnements",
      value: stats?.summary?.subscriptions || 0,
      icon: CreditCard,
      bgColor: "bg-violet-500/20",
      iconColor: "text-violet-400",
      description: "Paiements"
    },
    {
      title: "Conversion",
      value: `${stats?.summary?.conversion_rate || 0}%`,
      icon: Target,
      bgColor: "bg-amber-500/20",
      iconColor: "text-amber-400",
      description: "Visites → Inscrits"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
                  <FileText className="h-5 w-5 text-white" />
                </div>
                <span className="font-bold text-xl">SmartFacture AI</span>
              </Link>
              <Badge className="bg-violet-500/20 text-violet-300 border-violet-500/30">
                <BarChart3 className="h-3 w-3 mr-1" />
                Analytics Admin
              </Badge>
            </div>
            
            <div className="flex items-center gap-3">
              <select
                value={period}
                onChange={(e) => setPeriod(Number(e.target.value))}
                className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
              >
                <option value={1}>Aujourd'hui</option>
                <option value={7}>7 derniers jours</option>
                <option value={30}>30 derniers jours</option>
                <option value={90}>90 derniers jours</option>
              </select>
              
              <Button 
                onClick={fetchAllData} 
                variant="outline" 
                size="sm"
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
            </div>
          </div>
          
          {lastUpdate && (
            <p className="text-xs text-slate-500 mt-2">
              Dernière mise à jour : {lastUpdate.toLocaleTimeString('fr-FR')}
            </p>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {kpis.map((kpi, idx) => {
            const Icon = kpi.icon;
            return (
              <Card key={idx} className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-slate-400 mb-1">{kpi.title}</p>
                      <p className="text-3xl font-bold text-white">{formatNumber(kpi.value)}</p>
                      <p className="text-xs text-slate-500 mt-1">{kpi.description}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl ${kpi.bgColor} flex items-center justify-center`}>
                      <Icon className={`h-6 w-6 ${kpi.iconColor}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Main Grid */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Funnel */}
          <Card className="lg:col-span-2 bg-slate-900/50 border-slate-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Target className="h-5 w-5 text-blue-400" />
                Entonnoir de conversion
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {funnel?.funnel?.map((step, idx) => {
                  const icons = {
                    'page_view': Eye,
                    'signup': UserPlus,
                    'trial_start': Zap,
                    'subscription': CreditCard
                  };
                  const Icon = icons[step.step] || Activity;
                  const maxCount = funnel.funnel[0]?.count || 1;
                  const percentage = maxCount > 0 ? (step.count / maxCount * 100) : 0;
                  
                  return (
                    <div key={step.step}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-slate-400" />
                          <span className="font-medium text-white">{step.label}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-2xl font-bold text-white">{step.count}</span>
                          {idx > 0 && (
                            <Badge className={`${step.conversion_rate > 10 ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'}`}>
                              {step.conversion_rate}%
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="h-8 bg-slate-800 rounded-lg overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg transition-all duration-700 flex items-center justify-end pr-3"
                          style={{ width: `${Math.max(percentage, 2)}%` }}
                        >
                          {percentage > 15 && (
                            <span className="text-xs font-medium text-white">{percentage.toFixed(0)}%</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {(!funnel?.funnel || funnel.funnel.every(s => s.count === 0)) && (
                <div className="text-center py-8">
                  <Activity className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">Pas encore de données</p>
                  <p className="text-sm text-slate-500">Les conversions apparaîtront ici</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Traffic Sources */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Globe className="h-5 w-5 text-emerald-400" />
                Sources de trafic
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.traffic_sources?.length > 0 ? (
                <div className="space-y-3">
                  {stats.traffic_sources.map((source, idx) => {
                    const maxCount = stats.traffic_sources[0]?.count || 1;
                    const percentage = (source.count / maxCount * 100);
                    return (
                      <div key={source.source}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-slate-300">{source.source}</span>
                          <span className="font-medium text-white">{source.count}</span>
                        </div>
                        <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Globe className="h-10 w-10 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400 text-sm">Aucune source UTM</p>
                  <p className="text-xs text-slate-500 mt-2">
                    Ajoute ?utm_source=xxx<br/>à tes liens
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Second Row */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Top Pages */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <MousePointer className="h-5 w-5 text-violet-400" />
                Pages les plus visitées
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.top_pages?.length > 0 ? (
                <div className="space-y-3">
                  {stats.top_pages.slice(0, 8).map((page, idx) => (
                    <div key={page.page} className="flex items-center justify-between py-2 border-b border-slate-800 last:border-0">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-slate-800 flex items-center justify-center text-xs text-slate-400">
                          {idx + 1}
                        </span>
                        <span className="text-slate-300 truncate max-w-[200px]">
                          {page.page || '/'}
                        </span>
                      </div>
                      <Badge className="bg-slate-800 text-slate-300">
                        {page.count} vues
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <MousePointer className="h-10 w-10 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400 text-sm">Aucune page vue</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Blog Performance */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <FileText className="h-5 w-5 text-cyan-400" />
                Performance Blog
              </CardTitle>
            </CardHeader>
            <CardContent>
              {blogStats?.articles?.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-cyan-400">{blogStats.total}</p>
                      <p className="text-sm text-slate-400">Articles publiés</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-emerald-400">
                        {blogStats.articles.reduce((sum, a) => sum + (a.views || 0), 0)}
                      </p>
                      <p className="text-sm text-slate-400">Vues totales</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-slate-400 mb-2">Articles les plus lus :</p>
                    {blogStats.articles
                      .sort((a, b) => (b.views || 0) - (a.views || 0))
                      .slice(0, 5)
                      .map((article) => (
                        <div key={article.slug} className="flex items-center justify-between py-1">
                          <span className="text-sm text-slate-300 truncate max-w-[220px]">
                            {article.title}
                          </span>
                          <span className="text-xs text-slate-500">{article.views || 0} vues</span>
                        </div>
                      ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <FileText className="h-10 w-10 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400 text-sm">Aucun article</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Tips */}
        <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/20">
          <CardContent className="p-6">
            <h3 className="font-bold text-white mb-4 flex items-center gap-2">
              <Zap className="h-5 w-5 text-amber-400" />
              Conseils pour augmenter tes conversions
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-slate-900/50 rounded-lg p-4">
                <h4 className="font-medium text-white mb-2">🔍 SEO & Google</h4>
                <p className="text-sm text-slate-400">
                  Soumets ton sitemap sur Google Search Console pour être indexé rapidement.
                </p>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-4">
                <h4 className="font-medium text-white mb-2">📱 Réseaux sociaux</h4>
                <p className="text-sm text-slate-400">
                  Partage tes articles sur LinkedIn avec le hashtag #efacturation2026
                </p>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-4">
                <h4 className="font-medium text-white mb-2">💰 Google Ads</h4>
                <p className="text-sm text-slate-400">
                  Lance une campagne ciblée "logiciel facturation 2026" pour des résultats immédiats.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Links */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Link to="/">
            <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
              Accueil
            </Button>
          </Link>
          <Link to="/blog">
            <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
              Blog
            </Button>
          </Link>
          <Link to="/dashboard">
            <Button className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600">
              Dashboard
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-6 mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center text-slate-500 text-sm">
          <p>SmartFacture AI Analytics • Données en temps réel</p>
        </div>
      </footer>
    </div>
  );
};

export default AdminAnalyticsPage;
